import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useIsOfferLocked } from '@/hooks/use-is-offer-locked';
import { DealItemWithVersions } from '@/lib/graphql/generated/graphql';
import { cn } from '@/lib/utils';
import { createSubRowSortingFn } from '@/lib/utils/table-filter-utils';
import { useSetColumnSort } from '@/states/column-sort.state';
import { dealItemDescriptionsByActiveVersionIdState } from '@/states/deal-item-descriptions.state';
import { Row } from '@tanstack/react-table';
import { useAtomValue } from 'jotai';
import { Pencil, TextAlignStart } from 'lucide-react';

import { ExtendedColumnDef } from '../table-products';
import { SortIcon } from './sort';
import { TableCellWithRecentlyModified } from './table-cell-with-recently-modified';

interface ColumnProps {
  openDescriptionEditModal: (
    activeVersionId: string,
    isLeafRow: boolean
  ) => void;
}

const useColumnDefDescription = ({
  openDescriptionEditModal,
}: ColumnProps): ExtendedColumnDef<DealItemWithVersions> => {
  const { t } = useTranslation();
  const descriptionsByActiveVersionId = useAtomValue(
    dealItemDescriptionsByActiveVersionIdState
  );
  const isOfferLocked = useIsOfferLocked();
  const { setColumnSort } = useSetColumnSort();

  const accessorKey = 'description' as const;

  const hasDescription = (row: Row<DealItemWithVersions>) => {
    return row.original.activeVersion.hasDescription;
  };

  const desc_IconOnly = (isLocked: boolean) => (
    <>
      <div className="flex items-center justify-center rounded-full bg-blue-100 p-1">
        <TextAlignStart
          className={cn('h-4 w-4 text-gray-500', !isLocked && 'cursor-pointer')}
        />
      </div>
      {!isLocked && (
        <Pencil className="h-4 w-4 shrink-0 cursor-pointer text-gray-500 opacity-0 transition-opacity duration-150 group-hover:opacity-100" />
      )}
    </>
  );

  return useMemo(() => {
    const getLeafDescriptionPreviewText = (
      row: Row<DealItemWithVersions>
    ): string => {
      const av = row.original.activeVersion;
      const raw = (
        av.description ??
        descriptionsByActiveVersionId?.[av.id] ??
        ''
      ).trim();
      return raw
        .replace(/<[^>]*>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
    };

    const desc_Tooltip = (
      row: Row<DealItemWithVersions>,
      previewText: string
    ) => {
      return hasDescription(row) ? (
        previewText ? (
          <p>{previewText}</p>
        ) : (
          <p>{t('kpi.description_tooltip_edit')}</p>
        )
      ) : (
        <p>{t('kpi.description_tooltip_add')}</p>
      );
    };

    return {
      accessorKey,
      name: t('kpi.description'),
      sortingFn: createSubRowSortingFn('description', (row) =>
        row.activeVersion?.hasDescription ? 'true' : 'false'
      ),
      header: () => (
        <div
          className="flex items-center"
          onClick={() => setColumnSort(accessorKey)}
        >
          {t('kpi.description')} <SortIcon pointer="description" />
        </div>
      ),
      cell: ({ row }) => {
        const isParent = row.subRows.length > 1;
        const avId = row.original.activeVersion.id;
        const leafPreviewText = getLeafDescriptionPreviewText(row);

        return (
          <TableCellWithRecentlyModified
            productId={row.original.id}
            field="description"
            isParent={isParent}
          >
            {(isRecentlyModified) => (
              <div
                className={cn(
                  'h-full w-full',
                  (isParent || isOfferLocked) && 'opacity-50',
                  isOfferLocked ? 'cursor-default' : 'cursor-pointer',
                  isRecentlyModified && 'border-l-2 border-blue-500 bg-blue-50'
                )}
                onClick={() => {
                  if (isParent || isOfferLocked) return;
                  void openDescriptionEditModal(avId, !isParent);
                }}
              >
                {!isParent ? (
                  <div
                    className={cn(
                      'flex h-full w-full items-center justify-center px-2 text-sm',
                      !isOfferLocked && 'group'
                    )}
                  >
                    {hasDescription(row) ? (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div
                            className={cn(
                              'flex w-full items-center justify-center gap-2',
                              isOfferLocked && 'cursor-default'
                            )}
                          >
                            {desc_IconOnly(isOfferLocked)}
                          </div>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-md" side="bottom">
                          {!isOfferLocked && (
                            <div>{desc_Tooltip(row, leafPreviewText)}</div>
                          )}
                        </TooltipContent>
                      </Tooltip>
                    ) : (
                      <div
                        className="flex min-h-9 w-full items-center justify-center"
                        aria-hidden
                      />
                    )}
                  </div>
                ) : (
                  <div className="flex h-full w-full min-w-0 items-center justify-start px-1 text-sm">
                    {(() => {
                      return isOfferLocked ? (
                        <p className="w-full text-left text-xs text-muted-foreground">
                          —
                        </p>
                      ) : (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <p className="text-xs text-muted-foreground">—</p>
                          </TooltipTrigger>
                          <TooltipContent className="max-w-md" side="bottom">
                            <p className="whitespace-pre-wrap break-words text-xs">
                              {t('kpi.description_tooltip_add_update_decl')}
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      );
                    })()}
                  </div>
                )}
              </div>
            )}
          </TableCellWithRecentlyModified>
        );
      },
      size: 100,
    };
  }, [
    t,
    accessorKey,
    descriptionsByActiveVersionId,
    isOfferLocked,
    setColumnSort,
    openDescriptionEditModal,
  ]);
};

export default useColumnDefDescription;
